import {
  Component,
  Input,
  ElementRef,
  OnDestroy,
  ChangeDetectionStrategy,
  HostListener,
  ViewEncapsulation
} from '@angular/core';
import {
  Overlay,
  OverlayRef,
  FlexibleConnectedPositionStrategy,
  ConnectedPosition,
  ScrollStrategy
} from '@angular/cdk/overlay';
import { TemplatePortal } from '@angular/cdk/portal';
import { ViewChild, ViewContainerRef } from '@angular/core';

@Component({
  standalone: false,
  selector: 'app-help-popover',
  template: `
    <button
      type="button"
      class="help-trigger"
      (click)="toggle()"
      aria-label="info"
    >
      <mat-icon fontIcon="info"></mat-icon>
    </button>

    <ng-template #popover>
      <div class="help-popover-panel">
        <div class="help-popover__header" *ngIf="title">
          <span class="help-popover__title">{{ title }}</span>
          <button
            mat-icon-button
            type="button"
            (click)="close()"
            aria-label="close"
          >
            <mat-icon fontIcon="close"></mat-icon>
          </button>
        </div>
        <div class="help-popover__body">
          {{ text }}
        </div>
      </div>
    </ng-template>
  `,
  changeDetection: ChangeDetectionStrategy.OnPush,
  encapsulation: ViewEncapsulation.None,
  styles: [
    `
      .help-popover-wrapper {
        border-radius: 14px;
        border: 1px solid rgba(0, 0, 0, 0.08);
        box-shadow: 0 14px 30px rgba(15, 23, 42, 0.18);
        background: var(--app-surface, #fff);
        padding: 0 !important;
        max-width: 320px;
        animation: helpPopoverFade 110ms ease-out;
        z-index: 1050;
      }

      .help-popover-panel {
        padding: 12px 14px;
        width: max-content;
        max-width: 320px;
      }

      .help-popover__header {
        display: flex;
        align-items: center;
        justify-content: space-between;
        gap: 8px;
        margin-bottom: 6px;
      }

      .help-popover__title {
        font-weight: 600;
        color: var(--app-text, #0f172a);
        font-size: 14px;
      }

      .help-popover__body {
        font-size: 13px;
        color: var(--app-muted, #475569);
        line-height: 1.4;
      }

      .help-trigger {
        display: inline-flex;
        align-items: center;
        justify-content: center;
        width: 20px;
        height: 20px;
        min-width: 20px;
        min-height: 20px;
        line-height: 20px;
        padding: 0;
        border: 0;
        border-radius: 999px;
        background: transparent;
        cursor: pointer;
        opacity: 0.7;
        transition: opacity 120ms ease;
        --mdc-icon-button-state-layer-size: 20px;
        --mdc-icon-button-icon-size: 18px;
      }

      .help-trigger:hover {
        opacity: 1;
      }

      .help-trigger:hover {
        opacity: 1;
      }

      @keyframes helpPopoverFade {
        from {
          opacity: 0;
          transform: translateY(-2px) scale(0.98);
        }
        to {
          opacity: 1;
          transform: translateY(0) scale(1);
        }
      }
    `
  ]
})
export class HelpPopoverComponent implements OnDestroy {
  @Input() title: string = '';
  @Input() text: string = '';
  @Input() maxWidth: number = 320;
  @Input() placement: 'bottom-start' | 'bottom' | 'top' = 'bottom-start';

  @ViewChild('popover') popoverTemplate!: any;

  private overlayRef: OverlayRef | null = null;

  constructor(
    private overlay: Overlay,
    private vcr: ViewContainerRef,
    private host: ElementRef<HTMLElement>
  ) {}

  toggle(): void {
    if (this.overlayRef && this.overlayRef.hasAttached()) {
      this.close();
      return;
    }
    this.open();
  }

  open(): void {
    if (!this.popoverTemplate) return;
    const positions: ConnectedPosition[] = [
      {
        originX: 'start',
        originY: 'bottom',
        overlayX: 'start',
        overlayY: 'top',
        offsetY: 6
      },
      {
        originX: 'start',
        originY: 'top',
        overlayX: 'start',
        overlayY: 'bottom',
        offsetY: -6
      }
    ];

    const strategy: FlexibleConnectedPositionStrategy = this.overlay
      .position()
      .flexibleConnectedTo(this.host)
      .withPositions(positions)
      .withPush(true);

    const scrollStrategy: ScrollStrategy = this.overlay.scrollStrategies.reposition();

    this.overlayRef = this.overlay.create({
      positionStrategy: strategy,
      hasBackdrop: true,
      backdropClass: 'cdk-overlay-transparent-backdrop',
      panelClass: ['help-popover-wrapper'],
      scrollStrategy
    });

    const portal = new TemplatePortal(this.popoverTemplate, this.vcr);
    this.overlayRef.attach(portal);

    this.overlayRef.backdropClick().subscribe(() => this.close());
    this.overlayRef.keydownEvents().subscribe(event => {
      if (event.key === 'Escape') {
        this.close();
      }
    });
  }

  close(): void {
    if (this.overlayRef) {
      this.overlayRef.detach();
    }
  }

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent): void {
    if (!this.overlayRef || !this.overlayRef.hasAttached()) return;
    const target = event.target as HTMLElement;
    if (this.host.nativeElement.contains(target)) return;
    if (this.overlayRef.overlayElement.contains(target)) return;
    this.close();
  }

  ngOnDestroy(): void {
    if (this.overlayRef) {
      this.overlayRef.dispose();
      this.overlayRef = null;
    }
  }
}
